import { faCheck, faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Accordion, Badge, Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { RECOMMENDED_BADGE_COPY } from '@/features/compliance/plan-pricing/recommendedPlan.copy'
import { displayRowLabel } from './lib/taxFilingRows'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { CADENCE_SUFFIX } from './lib/helpers'
import { FeatureDetailLines } from './FeatureDetailLines'
import type { PlanCadence } from './lib/planChange.types'

/** Corner radius for the plan-change table and drawer panels, between the `2xl` and `3xl` tokens. The cards use `rounded-xl`. */
export const CARD_RADIUS = 'rounded-[20px]'

export const PLAN_CARD_SHELL =
  'flex flex-1 basis-[272px] max-w-[352px] flex-col rounded-xl border p-6'

/**
 * Holds the Recommended badge. On a narrow card the slot is a full row above the title, kept on every card in the
 * view so the titles line up. Once the card's content box is 250px wide (a 300px card) it collapses and the badge
 * sits in the title row's right corner.
 */
export const BadgeSlot = ({
  reserve,
  isRecommended,
}: {
  reserve: boolean
  isRecommended: boolean
}) => {
  if (!reserve && !isRecommended) return null
  return (
    <div className="mb-2 flex h-7 items-center @[250px]:mb-0 @[250px]:h-0">
      {isRecommended && (
        <Badge
          color="green"
          className="@[250px]:absolute @[250px]:right-6 @[250px]:top-[26px]"
        >
          {RECOMMENDED_BADGE_COPY}
        </Badge>
      )}
    </div>
  )
}

/** Elevation on hover marks a card as actionable, so non-interactive cards do not take it. */
export const PLAN_CARD_HOVER =
  'hover:shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.10)]'

export interface CardPriceProps {
  effectiveAmountCents: number
  listAmountCents: number
  cadence: PlanCadence
  strikeListPrice: boolean
}

export const CardPrice = ({
  effectiveAmountCents,
  listAmountCents,
  cadence,
  strikeListPrice,
}: CardPriceProps) => (
  <div className="flex flex-col gap-0.5">
    <div className="flex items-baseline gap-1">
      <Text variant={TypographyStyles.DISPLAY_MD}>
        {formatCurrencyFromCents(effectiveAmountCents)}
      </Text>
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        {CADENCE_SUFFIX[cadence]}
      </Text>
    </div>
    {strikeListPrice && (
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="line-through"
      >
        {formatCurrencyFromCents(listAmountCents)}
        {CADENCE_SUFFIX[cadence]}
      </Text>
    )}
  </div>
)

type FeatureTone = 'gain' | 'loss' | 'keep'

const FEATURE_ICON = {
  gain: faPlus,
  loss: faMinus,
  keep: faCheck,
} as const

const FEATURE_ICON_COLOR = {
  gain: 'text-green-100',
  loss: 'text-neutral-500',
  keep: 'text-green-100',
} as const

export interface CardFeatureListProps {
  eyebrow?: string
  eyebrowClassName?: string
  featureIds: string[]
  tone: FeatureTone
  showDetailLines?: boolean
}

export const CardFeatureList = ({
  eyebrow,
  eyebrowClassName,
  featureIds,
  tone,
  showDetailLines = false,
}: CardFeatureListProps) => (
  <div className="flex flex-col gap-2">
    {eyebrow && (
      <Text
        variant={TypographyStyles.EYEBROW}
        className={mtc('text-neutral-500', eyebrowClassName)}
      >
        {eyebrow}
      </Text>
    )}
    <ul className="flex flex-col gap-[9px]">
      {featureIds.map((featureId) => (
        <li key={featureId} className="flex items-start gap-2">
          <Icon
            icon={FEATURE_ICON[tone]}
            color={FEATURE_ICON_COLOR[tone]}
            className="mt-[3px] text-sm"
          />
          <div className="flex flex-col">
            <Text
              variant={TypographyStyles.BODY_SM}
              className={tone === 'gain' ? 'font-medium' : undefined}
            >
              {displayRowLabel(featureId)}
            </Text>
            {showDetailLines && <FeatureDetailLines featureId={featureId} />}
          </div>
        </li>
      ))}
    </ul>
  </div>
)

export const PlanDetailsAccordion = (listProps: CardFeatureListProps) => (
  <Accordion type="single" collapsible variant="text">
    <Accordion.Item value="details">
      <Accordion.Trigger>View details</Accordion.Trigger>
      <Accordion.Content>
        <CardFeatureList {...listProps} showDetailLines />
      </Accordion.Content>
    </Accordion.Item>
  </Accordion>
)
